import React from 'react';
import { Row, Col, Card } from 'reactstrap';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import SubHeader from '../../components/Navbars/SubHeader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CARD_HEIGHT = '500px';

const getThemeTokens = (isLightTheme) => ({
  cardBg: isLightTheme ? '#ffffff' : '#27293d',
  cardBorder: isLightTheme ? '#eee' : '#3c3f58',
  headerBg: isLightTheme ? '#fff' : '#27293d',
  headerBorder: isLightTheme ? '#f0f0f0' : '#3c3f58',
  titleColor: isLightTheme ? '#333' : '#f1f5f9',
  rowColor: isLightTheme ? '#444' : 'rgba(255, 255, 255, 0.75)',
  valueColor: isLightTheme ? '#333' : '#ffffff',
  emphasisColor: isLightTheme ? '#000' : '#ffffff',
  dividerBorder: isLightTheme ? '#f0f0f0' : '#3c3f58',
  scrollbarThumb: isLightTheme ? '#eee' : '#45486a',
  loadingOverlay: isLightTheme ? 'rgba(255,255,255,0.25)' : 'rgba(30, 30, 47, 0.55)',
  labelPluginFill: isLightTheme ? '#555' : '#cbd5e1',
  gridColor: isLightTheme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)',
  tickColor: isLightTheme ? '#666' : '#94a3b8',
  tooltipBg: isLightTheme ? '#1f2937' : '#0f172a',
  tooltipTitle: isLightTheme ? '#f8fafc' : '#f1f5f9',
  tooltipBody: isLightTheme ? '#e2e8f0' : '#cbd5e1',
  tooltipBorder: isLightTheme ? '#334155' : '#1e293b',
  shadow: isLightTheme ? '0 2px 4px rgba(0,0,0,0.05)' : '0 2px 8px rgba(0,0,0,0.25)',
});

const CHART_PALETTES = {
  location: {
    light: ['#e3f2fd', '#bbdefb', '#90caf9', '#0092e0'],
    dark: ['#1e3a5f', '#2563eb', '#3b82f6', '#60a5fa'],
  },
  make: {
    light: ['#f1f8e9', '#dcedc8', '#c5e1a5', '#aed581'],
    dark: ['#365314', '#4d7c0f', '#65a30d', '#84cc16'],
  },
  status: {
    light: ['#9575cd', '#4db6ac', '#e57373', '#ffd54f', '#0092e0'],
    dark: ['#7c3aed', '#14b8a6', '#f87171', '#fbbf24', '#38bdf8'],
  },
  avgDol: {
    light: '#7ed957',
    dark: '#4ade80',
  },
};

const InventoryDashboard = ({
  statistics,
  activeColor,
  isLoading,
  shellThemeVariant = '1',
  isLightTheme = false,
}) => {
  const theme = React.useMemo(() => getThemeTokens(isLightTheme), [isLightTheme]);

  const formatValue = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
  };

  const chartAnimation = React.useMemo(() => ({
    duration: 1000,
    easing: 'easeOutQuart',
    delay: (context) => {
      let delay = 0;
      if (context.type === 'data' && context.mode === 'default') {
        delay = context.dataIndex * 80 + context.datasetIndex * 100;
      }
      return delay;
    },
  }), []);

  const normalizeData = (labels = [], values = [], isAvg = false, counts = []) => {
    const grouped = {};
    if (!labels || labels.length === 0) return { labels: ['-', '-', '-'], values: [0, 0, 0] };

    labels.forEach((label, i) => {
      if (!label) return;
      const canonical = label.trim().toLowerCase().split('-').map(part =>
        part.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      ).join('-');

      const val = values[i] || 0;
      const count = (counts && counts[i]) || 1;

      if (!grouped[canonical]) grouped[canonical] = { val: 0, count: 0 };

      if (isAvg) {
        grouped[canonical].val += val * count;
        grouped[canonical].count += count;
      } else {
        grouped[canonical].val += val;
      }
    });

    const resultLabels = Object.keys(grouped).sort((a, b) => grouped[b].val - grouped[a].val);
    const resultValues = resultLabels.map(l => {
      if (isAvg) return Math.round(grouped[l].val / (grouped[l].count || 1));
      return grouped[l].val;
    });
    return { labels: resultLabels, values: resultValues };
  };

  const barDataLabelPlugin = React.useMemo(() => ({
    id: 'datapoint-labels',
    afterDatasetsDraw: (chart) => {
      const { ctx, data } = chart;
      ctx.save();
      ctx.font = 'bold 10px Arial';
      ctx.fillStyle = theme.labelPluginFill;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      const meta = chart.getDatasetMeta(0);
      meta.data.forEach((bar, index) => {
        const val = data.datasets[0].data[index];
        if (val !== undefined) {
          ctx.fillText(`${val}`, bar.x + 5, bar.y);
        }
      });
      ctx.restore();
    },
  }), [theme.labelPluginFill]);

  const getBarChartOptions = React.useCallback((xSuggestedMax, xTickOptions = {}) => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    animation: chartAnimation,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: theme.tooltipBg,
        titleColor: theme.tooltipTitle,
        bodyColor: theme.tooltipBody,
        borderColor: theme.tooltipBorder,
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        suggestedMax: xSuggestedMax,
        grid: { borderDash: [2, 2], color: theme.gridColor },
        ticks: { font: { size: 10 }, color: theme.tickColor, ...xTickOptions },
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: theme.tickColor },
      },
    },
  }), [chartAnimation, theme]);

  const renderStatsList = (data) => (
    <div className="old-style-list">
      <div className="old-style-row"><span className="old-style-label"># Vehicles In Stock (Active)</span><span className="old-style-value">{data?.total_active_vehicles || 0}</span></div>
      <div className="old-style-row"><span className="old-style-label">Base Inventory Cost</span><span className="old-style-value">{formatValue(data?.base_inventory_cost)}</span></div>
      <div className="old-style-row"><span className="old-style-label">Expenses</span><span className="old-style-value">{formatValue(data?.total_expenses)}</span></div>
      <div
        className="old-style-row old-style-row--divider"
        style={{ borderBottom: `1px solid ${theme.dividerBorder}`, paddingBottom: '3px', marginBottom: '5px' }}
      >
        <span className="old-style-label">Total Inventory Cost</span>
        <span className="old-style-value old-style-value--emphasis">{formatValue((data?.base_inventory_cost || 0) + (data?.total_expenses || 0))}</span>
      </div>
      <div className="old-style-row"><span className="old-style-label">Vehicles Added Today</span><span className="old-style-value">{data?.added_today_count || 0}</span></div>
      <div className="old-style-row"><span className="old-style-label">Vehicle Value</span><span className="old-style-value">{formatValue(data?.added_today_value)}</span></div>
    </div>
  );

  const paletteKey = isLightTheme ? 'light' : 'dark';

  return (
    <div className="inventory-dashboard-container">
      <SubHeader
        activeColor={activeColor}
        back="/dashboard"
        title="Inventory Dashboard"
        theme2ScrollGapFill={shellThemeVariant === '2'}
        isLightTheme={isLightTheme}
      />

      <style>{`
        .inventory-dashboard-container .avg-on-lot-card {
          background: ${theme.cardBg};
          border: 1px solid ${theme.cardBorder};
          margin-bottom: 20px;
          height: ${CARD_HEIGHT};
          display: flex;
          flex-direction: column;
          box-shadow: ${theme.shadow};
          overflow: hidden;
          position: relative;
        }
        .inventory-dashboard-container .avg-on-lot-header {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px 20px;
          border-bottom: 1px solid ${theme.headerBorder};
          background: ${theme.headerBg};
          flex-shrink: 0;
        }
        .inventory-dashboard-container .avg-on-lot-title {
          font-weight: 600;
          font-size: 13px;
          color: ${theme.titleColor};
          margin: 0;
        }
        .inventory-dashboard-container .card-inner-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          padding: 12px;
          position: relative;
        }
        .inventory-dashboard-container .scrollable-chart-area {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 5px;
          scrollbar-width: thin;
        }
        .inventory-dashboard-container .scrollable-chart-area::-webkit-scrollbar {
          width: 5px;
        }
        .inventory-dashboard-container .scrollable-chart-area::-webkit-scrollbar-thumb {
          background: ${theme.scrollbarThumb};
        }
        .inventory-dashboard-container .old-style-list {
          padding: 0px 5px 8px 5px;
          flex-shrink: 0;
        }
        .inventory-dashboard-container .old-style-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 2px;
          font-size: 12px;
          color: ${theme.rowColor};
        }
        .inventory-dashboard-container .old-style-label {
          font-weight: 400;
        }
        .inventory-dashboard-container .old-style-value {
          font-weight: 600;
          color: ${theme.valueColor};
        }
        .inventory-dashboard-container .old-style-value--emphasis {
          color: ${theme.emphasisColor};
        }
        .inventory-dashboard-container .loading-ghost-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: ${theme.loadingOverlay};
          backdrop-filter: blur(1px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          pointer-events: none;
        }
        .inventory-dashboard-container .pulse-loading {
          font-weight: 600;
          font-size: 13px;
          color: #1d8cf8;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          animation: inventory-dashboard-pulse 1.5s infinite ease-in-out;
        }
        @keyframes inventory-dashboard-pulse {
          0% { opacity: 0.4; transform: scale(0.98); }
          50% { opacity: 1; transform: scale(1); }
          100% { opacity: 0.4; transform: scale(0.98); }
        }
      `}</style>

      <Row className="d-flex align-items-stretch">
        <Col lg="3" md="6" className="mb-4">
          <Card className="avg-on-lot-card">
            <div className="avg-on-lot-header"><h4 className="avg-on-lot-title">Avg Days On Lot</h4></div>
            <div className="card-inner-body">
              {isLoading && <div className="loading-ghost-overlay"><div className="pulse-loading">Loading...</div></div>}
              <div className="scrollable-chart-area">
                {(() => {
                  const raw = (statistics?.avg_days_on_lot_by_make || { labels: [], avg: [], count: [] });
                  const { labels: finalLabels, values: finalAvg } = normalizeData(raw.labels, raw.avg, true, raw.count);
                  const innerHeight = Math.max(400, finalLabels.length * 22);
                  return (
                    <div style={{ height: `${innerHeight}px`, position: 'relative' }}>
                      <Bar
                        key={isLoading ? 'loading-avg' : 'ready-avg'}
                        data={{
                          labels: finalLabels,
                          datasets: [{
                            label: 'Avg DOL',
                            data: finalAvg,
                            backgroundColor: CHART_PALETTES.avgDol[paletteKey],
                            borderWidth: 0,
                            barPercentage: 0.8,
                            categoryPercentage: 0.9,
                          }],
                        }}
                        options={getBarChartOptions((Math.max(...finalAvg, 0) * 1.5) + 30)}
                        plugins={[barDataLabelPlugin]}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          </Card>
        </Col>

        <Col lg="3" md="6" className="mb-4">
          <Card className="avg-on-lot-card">
            <div className="avg-on-lot-header"><h4 className="avg-on-lot-title">Inventory By Location</h4></div>
            <div className="card-inner-body">
              {isLoading && <div className="loading-ghost-overlay"><div className="pulse-loading">Loading...</div></div>}
              {renderStatsList(statistics?.inventory_by_location)}
              <div className="scrollable-chart-area">
                {(() => {
                  const raw = (statistics?.inventory_by_location || { labels: [], values: [] });
                  const { labels: finalLabels, values: finalValues } = normalizeData(raw.labels, raw.values);
                  const innerHeight = Math.max(220, finalLabels.length * 30);
                  const maxVal = Math.max(...finalValues, 0);
                  return (
                    <div style={{ height: `${innerHeight}px`, position: 'relative' }}>
                      <Bar
                        key={isLoading ? 'loading-location' : 'ready-location'}
                        data={{
                          labels: finalLabels,
                          datasets: [{
                            data: finalValues,
                            backgroundColor: CHART_PALETTES.location[paletteKey],
                            borderWidth: 0,
                            barPercentage: 0.8,
                            categoryPercentage: 0.9,
                          }],
                        }}
                        options={getBarChartOptions(
                          (maxVal * 1.6) + 15,
                          { stepSize: 1, precision: 0, callback: (v) => (Number.isInteger(v) ? v : null) }
                        )}
                        plugins={[barDataLabelPlugin]}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          </Card>
        </Col>

        <Col lg="3" md="6" className="mb-4">
          <Card className="avg-on-lot-card">
            <div className="avg-on-lot-header"><h4 className="avg-on-lot-title">Inventory By Make</h4></div>
            <div className="card-inner-body">
              {isLoading && <div className="loading-ghost-overlay"><div className="pulse-loading">Loading...</div></div>}
              {renderStatsList(statistics?.inventory_by_make)}
              <div className="scrollable-chart-area">
                {(() => {
                  const raw = (statistics?.inventory_by_make || { labels: [], values: [] });
                  const { labels: finalLabels, values: finalValues } = normalizeData(raw.labels, raw.values);
                  const innerHeight = Math.max(250, finalLabels.length * 28);
                  const maxVal = Math.max(...finalValues, 0);
                  return (
                    <div style={{ height: `${innerHeight}px`, position: 'relative' }}>
                      <Bar
                        key={isLoading ? 'loading-make' : 'ready-make'}
                        data={{
                          labels: finalLabels,
                          datasets: [{
                            data: finalValues,
                            backgroundColor: CHART_PALETTES.make[paletteKey],
                            borderWidth: 0,
                            barPercentage: 0.8,
                            categoryPercentage: 0.9,
                          }],
                        }}
                        options={getBarChartOptions(
                          (maxVal * 1.6) + 15,
                          { stepSize: 1, precision: 0, callback: (v) => (Number.isInteger(v) ? v : null) }
                        )}
                        plugins={[barDataLabelPlugin]}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          </Card>
        </Col>

        <Col lg="3" md="6" className="mb-4">
          <Card className="avg-on-lot-card">
            <div className="avg-on-lot-header"><h4 className="avg-on-lot-title">Inventory By Status</h4></div>
            <div className="card-inner-body">
              {isLoading && <div className="loading-ghost-overlay"><div className="pulse-loading">Loading...</div></div>}
              {renderStatsList(statistics?.inventory_by_status)}
              <div className="scrollable-chart-area">
                {(() => {
                  const raw = (statistics?.inventory_by_status || { labels: [], values: [] });
                  const { labels: finalLabels, values: finalValues } = normalizeData(raw.labels, raw.values);
                  const innerHeight = Math.max(200, finalLabels.length * 35);
                  const maxVal = Math.max(...finalValues, 0);
                  return (
                    <div style={{ height: `${innerHeight}px`, position: 'relative' }}>
                      <Bar
                        key={isLoading ? 'loading-status' : 'ready-status'}
                        data={{
                          labels: finalLabels,
                          datasets: [{
                            data: finalValues,
                            backgroundColor: CHART_PALETTES.status[paletteKey],
                            borderWidth: 0,
                            barPercentage: 0.8,
                            categoryPercentage: 0.9,
                          }],
                        }}
                        options={getBarChartOptions(
                          (maxVal * 1.5) + 20,
                          { stepSize: 1, precision: 0, callback: (v) => (Number.isInteger(v) ? v : null) }
                        )}
                        plugins={[barDataLabelPlugin]}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default InventoryDashboard;
